import React from "react";
import { useSearchParams } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import AccPanel from "@/components/AccPanel";
import ReferensiDesain from "@/components/ReferensiDesain";
import { apiAccRingkas, apiIzinRevisi, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rencanaTeks } from "@/lib/format";
import { saring, hitung, daftarCs, urutkan, URUTAN, lamaTunggu, statusTeks, revisiTeks, riwayatTeks } from "@/lib/accRingkas";
import GarisProgres from "@/components/GarisProgres";
import { ChevronDown, ChevronRight, RefreshCw, Search } from "lucide-react";
import * as W from "@/lib/waktu";

const WARNA = {  // tab colour of a row's status badge
  BARU: "bg-slate-800 text-slate-300", REFERENSI: "bg-slate-800 text-slate-300", MOCKUP: "bg-cyan-500/15 text-cyan-300",
  REVISI: "bg-amber-500/20 text-amber-300", REVISI_H1: "bg-amber-500/20 text-amber-300",
  PERLU_IZIN: "bg-red-500/15 text-red-300", REVISI_SIAP: "bg-soq-oranye/20 text-soq-biru-tua font-semibold",
  DISETUJUI: "bg-emerald-500/15 text-emerald-300",
};
const INGAT = "soq-acc-tab";  // remembered tab of this browser only (a convenience)

// v2.18 D: a revision past the limit waits here for the Owner or Manager (reason required, recorded).
function IzinRevisi({ r, onDone }) {
  const [alasan, setAlasan] = React.useState("");
  const [msg, setMsg] = React.useState(null);
  const kirim = async (e) => {
    e.preventDefault(); setMsg(null);
    try { await apiIzinRevisi(r.order_id, alasan); onDone(); }
    catch (e2) { setMsg(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
  };
  return (
    <form data-testid={`acc-izin-${r.nomor_faktur}`} onSubmit={kirim} className="flex flex-wrap items-center gap-2">
      <input value={alasan} onChange={(e) => setAlasan(e.target.value)} required minLength={5} maxLength={300}
        placeholder={`Alasan menyetujui revisi ke-${r.revisi_ke}`} className="min-w-[14rem] flex-1 rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100" />
      <button type="submit" className="rounded border border-amber-500/60 px-3 py-1.5 text-sm font-semibold text-amber-300">Setujui revisi</button>
      {msg && <span className="w-full text-xs text-red-300">{msg}</span>}
    </form>
  );
}

// the approval as AccPanel shows it (who, how, which mockup), from the latest approval in the history
const accDari = (r) => {
  const s = [...r.riwayat].reverse().find((h) => h.status === "DISETUJUI") || {};
  return { status: r.acc_status, catatan: r.catatan_revisi, nama: s.nama, sumber: s.sumber, versi_mockup: s.versi_mockup };
};

function Rincian({ r, bolehIzin, onChanged }) {
  return (
    <div data-testid={`acc-detail-${r.nomor_faktur}`} className="space-y-3 border-t border-slate-800 p-3 sm:p-4">
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400">
        <span>Nota <b className="font-mono text-slate-200">{r.nomor_faktur}</b></span>
        <span>SPK <b className="font-mono text-slate-200">{(r.nomor_spks?.length ? r.nomor_spks : [r.nomor_spk]).filter(Boolean).join(", ") || "—"}</b></span>
        <span>CS <b className="text-slate-200">{r.cs_nama || "—"}</b></span>
        <span>Dibuat {W.waktu(r.created_at)}</span>
        <span>Rencana selesai {rencanaTeks(r)}</span>
        {r.referensi_jumlah > 0 && <span>{r.referensi_jumlah} gambar referensi</span>}
      </div>
      {r.progres && <div className="rounded-lg border border-slate-800 bg-white p-2"><GarisProgres progres={r.progres} testid={`acc-garis-${r.nomor_faktur}`} /></div>}
      {r.revisi_h1 && r.antrean_revisi_sejak && <p className="text-xs text-slate-500">Revisi H+1 masuk {W.waktu(r.antrean_revisi_sejak)}: dikerjakan setelah antrean revisi hari itu. Rencana selesai tidak berubah otomatis.</p>}
      {r.perlu_izin && (
        <div data-testid={`acc-perlu-izin-${r.nomor_faktur}`} className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 space-y-2 text-sm">
          <p className="text-red-200">Revisi ke-{r.revisi_ke} melewati batas {r.batas_revisi} kali. Mockup baru bisa dikirim setelah Owner/Manager menyetujui dengan alasan.</p>
          {bolehIzin ? <IzinRevisi r={r} onDone={onChanged} /> : <p className="text-xs text-red-300">Minta persetujuan Owner atau Manager.</p>}
        </div>
      )}
      <AccPanel orderId={r.order_id} jenis={r.jenis} acc={accDari(r)} onChanged={onChanged} testid={`acc-panel-${r.nomor_faktur}`} jumlahDesain={r.jumlah_desain || 1}
        uploadTerkunci={r.perlu_izin || r.produksi_jalan} revisiKe={r.revisi_ke} revisiSiap={r.revisi_siap} />
      <ReferensiDesain orderId={r.order_id} testid={`acc-ref-${r.nomor_faktur}`} />
      {r.riwayat.length > 0 && <div className="text-xs text-slate-500">{r.riwayat.map((h, j) => <span key={j} className="mr-3 inline-block">{W.waktu(h.waktu)} {riwayatTeks(h, r.label_versi)}</span>)}</div>}
    </div>
  );
}

function Baris({ r, buka, onBuka, bolehIzin, onChanged, sekarang }) {
  const lencana = <span className={`inline-block rounded px-2 py-0.5 text-xs ${WARNA[r.tahap] || ""}`}>{statusTeks(r)}</span>;
  const tunggu = r.menunggu_sejak ? <span title={W.waktu(r.menunggu_sejak)}>{lamaTunggu(r.menunggu_sejak, sekarang)}</span> : "—";
  const Panah = buka ? ChevronDown : ChevronRight;
  return (
    <div data-testid={`acc-row-${r.nomor_faktur}`} className={`rounded-lg border bg-slate-900/60 ${buka ? "border-amber-500/60" : "border-slate-800"}`}>
      <button type="button" data-testid={`acc-buka-${r.nomor_faktur}`} aria-expanded={buka} onClick={onBuka}
        className="w-full text-left px-3 py-2.5 hover:bg-slate-800/40 rounded-lg">
        {/* phone: three short lines */}
        <div className="md:hidden space-y-1">
          <div className="flex items-center gap-2">
            <Panah size={15} className="shrink-0 text-slate-500" />
            <span className="font-mono text-sm text-amber-300">{r.nomor_spk || r.nomor_faktur}</span>
            <span className="rounded border border-slate-700 px-1.5 text-[11px]">{r.jenis}</span>
            <span className="ml-auto text-xs text-slate-400">{tunggu}</span>
          </div>
          <div className="pl-6 text-sm text-slate-100 truncate">{r.customer || "—"}</div>
          <div className="pl-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-400">
            {lencana}<span>revisi {revisiTeks(r)}</span><span>selesai {rencanaTeks(r)}</span>
          </div>
        </div>
        {/* computer: one row per SPK */}
        <div className="hidden md:grid grid-cols-[1rem_10.5rem_minmax(0,1.4fr)_4rem_minmax(0,2fr)_6.5rem_7.5rem_6.5rem] items-center gap-3 text-sm">
          <Panah size={15} className="text-slate-500" />
          <span className="font-mono text-amber-300 whitespace-nowrap">{r.nomor_spk || "—"}<span className="block text-[11px] text-slate-500">{r.nomor_faktur}</span></span>
          <span className="truncate text-slate-100">{r.customer || "—"}</span>
          <span className="text-xs">{r.jenis}</span>
          <span className="min-w-0">{lencana}</span>
          <span className={`text-xs whitespace-nowrap ${r.revisi_ke > r.batas_revisi ? "text-red-300" : "text-slate-300"}`}>{revisiTeks(r)}</span>
          <span className="text-xs text-slate-300 whitespace-nowrap">{rencanaTeks(r)}</span>
          <span className="text-xs text-slate-300 whitespace-nowrap">{tunggu}</span>
        </div>
      </button>
      {buka && <Rincian r={r} bolehIzin={bolehIzin} onChanged={onChanged} />}
    </div>
  );
}

// F1.5-I: orders (Jersey & DTF) waiting for the customer's ACC — upload the mockup, send the ACC link,
// see revision requests. No prices on this page.
// v2.19 (owner 2026-10-25): one row per SPK in tabs with counts; the details open on a click.
export default function AccMockup() {
  const { user } = useAuth();
  const bolehIzin = user?.role === "admin" || user?.role === "manager";
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  // v2.19: the bell links here with ?tab=REVISI_SIAP&cari=<SPK>
  const [params] = useSearchParams();
  const [tab, setTab] = React.useState(() => { try { return params.get("tab") || localStorage.getItem(INGAT) || "BARU"; } catch { return params.get("tab") || "BARU"; } });
  const [cari, setCari] = React.useState(() => params.get("cari") || "");
  const [jenis, setJenis] = React.useState("");
  const [cs, setCs] = React.useState("");
  const [urut, setUrut] = React.useState("TUNGGU");
  const [buka, setBuka] = React.useState(null);
  const [sekarang, setSekarang] = React.useState(Date.now());
  const load = React.useCallback(() => apiAccRingkas().then((x) => { setData(x); setError(null); setSekarang(Date.now()); })
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message)), []);
  React.useEffect(() => { load(); }, [load]);
  // the bell may link here while the page is already open: follow the new address with fresh data
  const tujuan = `${params.get("tab") || ""}|${params.get("cari") || ""}`;
  const awal = React.useRef(true);
  React.useEffect(() => {
    if (awal.current) { awal.current = false; return; }
    if (params.get("tab")) { setTab(params.get("tab")); setBuka(null); }
    if (params.get("cari") !== null) setCari(params.get("cari"));
    load();
  }, [tujuan]);  // eslint-disable-line react-hooks/exhaustive-deps
  // after a change (new mockup, approval) the open row may move to another tab: follow it
  React.useEffect(() => {
    const r = buka && data?.baris.find((x) => x.order_id === buka);
    if (r && r.tahap !== tab) setTab(r.tahap);
  }, [data]);  // eslint-disable-line react-hooks/exhaustive-deps
  const pilihTab = (k) => { setTab(k); setBuka(null); try { localStorage.setItem(INGAT, k); } catch { /* private window */ } };

  const tahap = data?.tahap || [];
  const tersaring = saring(data?.baris, { cari, jenis, cs });
  const jumlah = hitung(tersaring, tahap);
  const isi = urutkan(tersaring.filter((r) => r.tahap === tab), urut);
  const lain = tahap.filter((t) => t.kode !== tab && jumlah[t.kode] > 0);
  const label = (k) => tahap.find((t) => t.kode === k)?.label || k;
  const pilihCs = daftarCs(data?.baris);
  const ctl = "rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100";

  return (
    <div data-testid="acc-mockup-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">ACC & Mockup</h1>
          <p className="text-sm text-slate-400 mt-1">Pesanan Jersey & DTF yang menunggu persetujuan desain customer, satu baris per pesanan (pesanan Jersey yang dipecah per desain menampilkan semua SPK-nya). Klik baris untuk upload mockup, kirim link ACC, atau melihat revisi.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative flex-1 min-w-[12rem]">
            <Search size={15} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="acc-cari" value={cari} onChange={(e) => { setCari(e.target.value); setBuka(null); }}
              placeholder="Cari customer, nomor SPK, atau nomor nota" className={`${ctl} w-full pl-8`} />
          </label>
          <select data-testid="acc-jenis" value={jenis} onChange={(e) => setJenis(e.target.value)} className={ctl} aria-label="Kategori">
            <option value="">Semua kategori</option><option value="JERSEY">Jersey</option><option value="DTF">DTF</option>
          </select>
          <select data-testid="acc-cs" value={cs} onChange={(e) => setCs(e.target.value)} className={ctl} aria-label="Kasir/CS">
            <option value="">Semua CS</option>{pilihCs.map((c) => <option key={c.id} value={c.id}>{c.nama}</option>)}
          </select>
          <select data-testid="acc-urut" value={urut} onChange={(e) => setUrut(e.target.value)} className={ctl} aria-label="Urutan">
            {URUTAN.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <button onClick={load} className="text-xs text-slate-400 inline-flex items-center gap-1 px-1"><RefreshCw size={13} /> Muat ulang</button>
        </div>
        <div role="tablist" className="-mx-4 px-4 sm:mx-0 sm:px-0 flex gap-1.5 overflow-x-auto pb-1 md:flex-wrap md:overflow-visible">
          {tahap.map((t) => (
            <button key={t.kode} role="tab" aria-selected={tab === t.kode} data-testid={`acc-tab-${t.kode}`} onClick={() => pilihTab(t.kode)}
              className={`shrink-0 whitespace-nowrap rounded-md border px-3 py-1.5 text-sm ${tab === t.kode ? "border-amber-500 bg-amber-500 text-slate-950 font-semibold" : "border-slate-700 text-slate-300"}`}>
              {t.label} <span data-testid={`acc-jumlah-${t.kode}`} className={`ml-1 rounded-full px-1.5 text-xs ${tab === t.kode ? "bg-slate-950/15" : "bg-slate-800"}`}>{jumlah[t.kode] || 0}</span>
            </button>
          ))}
        </div>
        {tab === "REVISI_H1" && <p className="text-xs text-slate-500">Revisi yang masuk setelah hari mockup dikirim: dikerjakan setelah revisi hari itu, urut waktu revisi masuk. Rencana selesai tidak berubah otomatis.</p>}
        {tab === "REVISI_SIAP" && <p className="text-xs text-slate-500">Desain sudah menekan Revisi selesai; customer sudah bisa melihat mockup baru di link ACC. Kirim ke customer lewat Kirim link ACC via WA atau Salin link, lalu pesanan pindah ke Sudah upload mockup.</p>}
        {tab === "DISETUJUI" && data && <p className="text-xs text-slate-500">Disetujui customer dalam {data.disetujui_hari} hari terakhir (Owner bisa mengubahnya di Pengaturan). Jersey yang belum di-ACC kasir tetap di sini sampai di-ACC.</p>}
        {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}
        {!data && !error && <p className="text-slate-500">Memuat…</p>}
        {data && (
          <div className="space-y-2">
            <div className="hidden md:grid grid-cols-[1rem_10.5rem_minmax(0,1.4fr)_4rem_minmax(0,2fr)_6.5rem_7.5rem_6.5rem] gap-3 px-3 text-xs font-semibold text-slate-500">
              <span /><span>SPK / Nota</span><span>Customer</span><span>Kategori</span><span>Status</span><span>Revisi</span><span>Rencana selesai</span><span>{tab === "DISETUJUI" ? "Disetujui" : "Menunggu"}</span>
            </div>
            {isi.length === 0 && (
              <div data-testid="acc-kosong" className="rounded-lg border border-slate-800 px-4 py-6 text-center text-sm text-slate-500">
                Tidak ada pesanan di tab {label(tab)}{cari || jenis || cs ? " dengan pencarian/saringan ini" : ""}.
                {lain.length > 0 && <div className="mt-2 flex flex-wrap justify-center gap-2">Ada di:
                  {lain.map((t) => <button key={t.kode} onClick={() => pilihTab(t.kode)} className="text-cyan-300 underline">{t.label} ({jumlah[t.kode]})</button>)}</div>}
              </div>
            )}
            {isi.map((r) => (
              <Baris key={r.order_id} r={r} buka={buka === r.order_id} onBuka={() => setBuka(buka === r.order_id ? null : r.order_id)}
                bolehIzin={bolehIzin} onChanged={load} sekarang={sekarang} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
